// ==================== GURUKUL — REQUESTS (own menu) ====================
// One inbox for every Gurukul request, laid out like Finance's Approval Portal: filter card, table
// (cards on mobile), click a row for the detail modal, and Approve / Reject / Ask clarification /
// Reply — every one of them asks for confirmation first.
import { showToast, showConfirm } from '../components/toasts.js';
import { renderLmsRequestForm } from './gurukul-lms.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const BADGE = { Open: 'badge-warning', 'Needs clarification': 'badge-info', Approved: 'badge-success', Rejected: 'badge-danger' };
const KIND_LABEL = { approve: 'Approved', reject: 'Rejected', clarify: 'Clarification asked', reply: 'Reply', edit: 'Edited', supersede: 'OPD decided (coordinator stage skipped)' };
const ACTIONS = {
  approve: { title: 'Approve this request?', confirm: 'Approve', cls: 'success', needText: false, hint: 'Optional comment for the record.' },
  reject: { title: 'Reject this request?', confirm: 'Reject', cls: 'danger', needText: true, hint: 'Reason (required) — the requester will see this.' },
  clarify: { title: 'Ask for clarification', confirm: 'Send question', cls: 'warning', needText: true, hint: 'Your question (required) — the request stays open until they reply.' },
  reply: { title: 'Reply to the clarification', confirm: 'Send reply', cls: 'success', needText: true, hint: 'Your reply (required).' },
};

let rows = [];
let currentId = null;

export function getGurukulRequestsPage() {
  return `
    <h1 class="page-title">Gurukul requests</h1>
    <div class="card filter-card">
      <div class="form-grid-row form-grid-row--portal-filters">
        <div class="form-group form-group--narrow">
          <label for="gkrStatus">Status</label>
          <select id="gkrStatus">
            <option value="open" selected>Submitted — waiting for a decision</option>
            <option value="clarification">Needs clarification</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All (old, new and current)</option>
          </select>
        </div>
        <div class="form-group form-group--narrow">
          <label for="gkrType">Type</label>
          <select id="gkrType">
            <option value="all">All</option>
            <option value="add_user">Add user</option>
            <option value="add_guardian">Add guardian</option>
            <option value="retire">Retire</option>
            <option value="lms">LMS</option>
          </select>
        </div>
        <div class="form-group">
          <label for="gkrTeam">Team</label>
          <select id="gkrTeam"><option value="">All teams</option></select>
        </div>
        <div class="form-group form-group--search">
          <label for="gkrSearch">Search</label>
          <input type="text" id="gkrSearch" placeholder="Request ID, name, email or learner ID" maxlength="80">
        </div>
        <div class="form-group form-group--action">
          <label>&nbsp;</label>
          <button type="button" id="gkrSearchBtn">Search</button>
        </div>
      </div>
    </div>

    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
        <h2 style="margin:0;">Requests</h2>
        <button type="button" class="secondary" id="gkrNewLms">+ New LMS request</button>
      </div>
      <div class="table-container show-desktop">
        <table class="table-stack-mobile">
          <thead><tr><th>Request</th><th>Type</th><th>About</th><th>Team</th><th>Submitted by</th><th>Date</th><th>Status</th></tr></thead>
          <tbody id="gkrBody"><tr><td colspan="7" class="empty-state">Loading…</td></tr></tbody>
        </table>
      </div>
      <div id="gkrMobile" class="show-mobile data-card-list"></div>
    </div>

    <div id="gkrDetailModal" class="modal">
      <div class="modal-content" style="max-width:720px;">
        <button type="button" class="close-modal" id="gkrDetailClose">&times;</button>
        <h2 id="gkrDetailTitle">Request</h2>
        <div id="gkrDetailBody"><p class="empty-state">Loading…</p></div>
        <div id="gkrDetailFooter" class="btn-group" style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;"></div>
      </div>
    </div>

    <div id="gkrActionModal" class="modal">
      <div class="modal-content" style="max-width:480px;padding:22px 26px;">
        <h2 id="gkrActionTitle"></h2>
        <p class="page-intro" id="gkrActionRef"></p>
        <div class="form-group">
          <label for="gkrActionText" id="gkrActionHint"></label>
          <textarea id="gkrActionText" rows="3" maxlength="2000"></textarea>
        </div>
        <div class="btn-group" style="display:flex;gap:8px;justify-content:flex-end;">
          <button type="button" class="secondary" id="gkrActionCancel">Cancel</button>
          <button type="button" id="gkrActionConfirm">Confirm</button>
        </div>
      </div>
    </div>
  `;
}

function badge(label) {
  return `<span class="badge ${BADGE[label] || 'badge-info'}">${esc(label)}</span>`;
}
function fmtDate(d) { return d ? new Date(d).toLocaleDateString() : ''; }

async function loadList() {
  const body = document.getElementById('gkrBody');
  const mobile = document.getElementById('gkrMobile');
  const qs = new URLSearchParams({
    status: document.getElementById('gkrStatus').value,
    type: document.getElementById('gkrType').value,
    team: document.getElementById('gkrTeam').value,
    q: document.getElementById('gkrSearch').value.trim(),
  });
  try {
    const data = await api('/api/gurukul/requests?' + qs.toString());
    rows = data.requests;
    const teamSel = document.getElementById('gkrTeam');
    const keep = teamSel.value;
    teamSel.innerHTML = '<option value="">All teams</option>' + data.teams.map(t => `<option value="${t.id}">${esc(t.name)}</option>`).join('');
    teamSel.value = data.teams.some(t => t.id === keep) ? keep : '';

    if (!rows.length) {
      body.innerHTML = '<tr><td colspan="7" class="empty-state">No requests match.</td></tr>';
      mobile.innerHTML = '<p class="empty-state">No requests match.</p>';
      return;
    }
    body.innerHTML = rows.map(r => `
      <tr class="row-clickable" data-id="${r.id}">
        <td data-label="Request"><strong>${esc(r.request_no)}</strong></td>
        <td data-label="Type">${esc(r.type_label)}</td>
        <td data-label="About">${esc(r.title)}${r.step ? ` <em>(${esc(r.step)})</em>` : ''}</td>
        <td data-label="Team">${esc(r.team)}</td>
        <td data-label="Submitted by">${esc(r.requested_by)}</td>
        <td data-label="Date">${fmtDate(r.created_at)}</td>
        <td data-label="Status">${badge(r.status_label)}</td>
      </tr>`).join('');
    mobile.innerHTML = rows.map(r => `
      <article class="data-card data-card--compact data-card--clickable" data-id="${r.id}">
        <div class="data-card-top"><span class="data-card-title">${esc(r.request_no)}</span>${badge(r.status_label)}</div>
        <div class="data-card-row"><span>Type</span><span>${esc(r.type_label)}</span></div>
        <div class="data-card-row"><span>About</span><span>${esc(r.title)}</span></div>
        <div class="data-card-row"><span>Team</span><span>${esc(r.team)}</span></div>
        <div class="data-card-row"><span>Date</span><span>${fmtDate(r.created_at)}</span></div>
      </article>`).join('');
    document.querySelectorAll('#gkrBody tr[data-id], #gkrMobile article[data-id]').forEach(el =>
      el.addEventListener('click', () => openDetail(el.dataset.id)));
  } catch (err) {
    body.innerHTML = `<tr><td colspan="7" class="empty-state">Failed to load: ${esc(err.message)}</td></tr>`;
    mobile.innerHTML = '';
  }
}

async function openDetail(id) {
  currentId = id;
  const modal = document.getElementById('gkrDetailModal');
  const body = document.getElementById('gkrDetailBody');
  const footer = document.getElementById('gkrDetailFooter');
  modal.classList.add('active');
  body.innerHTML = '<p class="empty-state">Loading…</p>';
  footer.innerHTML = '';
  let d;
  try { d = await api('/api/gurukul/requests?id=' + encodeURIComponent(id)); }
  catch (err) { body.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; return; }

  document.getElementById('gkrDetailTitle').innerHTML = `${esc(d.request.request_no)} — ${esc(d.request.type_label)} ${badge(d.request.status_label)}`;
  body.innerHTML = `
    <div class="table-container"><table class="table-stack-mobile"><tbody>
      ${d.fields.filter(f => f.label !== 'Request' && f.label !== 'Type' && f.label !== 'Status').map(f =>
        `<tr><th style="width:38%;text-align:left;">${esc(f.label)}</th><td data-label="${esc(f.label)}">${esc(f.value)}</td></tr>`).join('')}
    </tbody></table></div>
    <h3 style="margin-top:16px;">History</h3>
    ${d.timeline.length ? d.timeline.map(t => `
      <div style="border-left:3px solid var(--border);padding:4px 10px;margin-bottom:8px;">
        <strong>${esc(KIND_LABEL[t.kind] || t.kind)}</strong> · ${esc(t.author)} · ${new Date(t.created_at).toLocaleString()}
        ${t.body ? `<div>${esc(t.body)}</div>` : ''}
      </div>`).join('') : '<p class="empty-state">No comments yet.</p>'}`;

  const btns = [];
  if (d.can_act) {
    btns.push('<button type="button" class="success" data-act="approve">Approve</button>');
    btns.push('<button type="button" class="warning" data-act="clarify">Ask clarification</button>');
    btns.push('<button type="button" class="danger" data-act="reject">Reject</button>');
  }
  if (d.can_reply) btns.push('<button type="button" data-act="reply">Reply</button>');
  if (d.can_edit) btns.push('<button type="button" class="secondary" id="gkrEditBtn">Edit</button>');
  btns.push('<button type="button" class="secondary" id="gkrDetailClose2">Close</button>');
  footer.innerHTML = btns.join('');
  footer.querySelectorAll('[data-act]').forEach(b => b.addEventListener('click', () => openAction(b.dataset.act, d.request)));
  document.getElementById('gkrEditBtn')?.addEventListener('click', () => openEdit(d));
  document.getElementById('gkrDetailClose2').addEventListener('click', closeDetail);
}

function closeDetail() { document.getElementById('gkrDetailModal').classList.remove('active'); }

// ── New LMS request (Phase C) for coordinators / OPD: choose the learner, then the shared form ──
async function openNewLms() {
  let modal = document.getElementById('gkrLmsModal');
  if (!modal) { modal = document.createElement('div'); modal.id = 'gkrLmsModal'; modal.className = 'modal'; document.body.appendChild(modal); }
  modal.innerHTML = `<div class="modal-content" style="max-width:720px;">
    <button type="button" class="close-modal" id="gkrLmsClose">&times;</button>
    <h2>New LMS request</h2>
    <div class="form-group"><label for="gkrLmsLearner">Learner</label><select id="gkrLmsLearner" style="max-width:320px;width:100%;"><option value="">Loading…</option></select></div>
    <div id="gkrLmsForm"></div></div>`;
  modal.classList.add('active');
  document.getElementById('gkrLmsClose').onclick = () => modal.classList.remove('active');
  let learners = [];
  try { ({ learners } = await api('/api/gurukul/lms-requests?learners=1')); } catch (err) { showToast(err.message, 'error'); }
  const sel = document.getElementById('gkrLmsLearner');
  sel.innerHTML = learners.length ? '<option value="">— choose —</option>' + learners.map(l => `<option value="${l.id}">${esc(l.learner_id)}</option>`).join('')
    : '<option value="">No learners in your teams</option>';
  sel.onchange = () => {
    const box = document.getElementById('gkrLmsForm');
    if (!sel.value) { box.innerHTML = ''; return; }
    renderLmsRequestForm(box, sel.value, () => { modal.classList.remove('active'); loadList(); });
  };
}

// ── Edit a pending request (PLT-23): OPD any request, the submitter their own ──
function openEdit(d) {
  const e = d.edit;
  const v = e.values;
  let modal = document.getElementById('gkrEditModal');
  if (!modal) { modal = document.createElement('div'); modal.id = 'gkrEditModal'; modal.className = 'modal'; document.body.appendChild(modal); }
  const inp = (id, label, val, max = 60, type = 'text') => `<div class="form-group"><label for="${id}">${label}</label><input type="${type}" id="${id}" value="${esc(val ?? '')}" maxlength="${max}" style="max-width:320px;width:100%;"></div>`;
  let html = '';
  if (e.type === 'add_user') {
    html = `${inp('gkeFirst', 'First name', v.first_name)}${inp('gkeLast', 'Last name', v.last_name)}${inp('gkeEmail', 'Email', v.email, 254, 'email')}
      <div class="form-group"><label>Gender</label><div style="display:flex;gap:16px;">
        ${['female', 'male'].map(g => `<label style="display:flex;gap:6px;align-items:center;font-weight:normal;"><input type="radio" name="gkeGender" value="${g}"${v.gender === g ? ' checked' : ''}> ${g === 'female' ? 'Female' : 'Male'}</label>`).join('')}</div></div>
      <div class="form-group"><label>Roles</label><div style="display:flex;flex-wrap:wrap;gap:6px 16px;">
        ${[...new Set([...e.options.roles, ...v.roles])].map(r => `<label style="display:flex;gap:6px;align-items:center;font-weight:normal;"><input type="checkbox" class="gke-role" value="${esc(r)}"${v.roles.includes(r) ? ' checked' : ''}> ${esc(r)}</label>`).join('')}</div></div>
      <div class="form-group"><label for="gkeTeam">Team</label><select id="gkeTeam" style="max-width:320px;width:100%;"><option value="">— none —</option>
        ${e.options.teams.map(t => `<option value="${t.id}"${t.id === v.team_id ? ' selected' : ''}>${esc(t.name)}</option>`).join('')}</select></div>
      <div id="gkeKidsBox">
        ${inp('gkeKids', 'Number of children', v.num_children ?? 0, 2, 'number')}
        <div class="form-group"><label for="gkeProgram">Program</label><select id="gkeProgram" style="max-width:320px;width:100%;">
          ${e.options.programs.map(p => `<option value="${p.id}"${p.id === v.program_id ? ' selected' : ''}>${esc(p.name)}</option>`).join('')}</select></div>
        <div id="gkeChildRows"></div>
      </div>
      <div class="form-group"><label for="gkeNotes">Notes for the approver</label><textarea id="gkeNotes" rows="2" maxlength="1000" style="width:100%;max-width:560px;">${esc(v.approver_notes || '')}</textarea></div>`;
  } else if (e.type === 'add_guardian') {
    html = `${inp('gkeFirst', 'First name', v.first_name)}${inp('gkeLast', 'Last name', v.last_name)}${inp('gkeEmail', 'Email', v.email, 254, 'email')}${inp('gkeRel', 'Relationship', v.relationship_label, 50)}`;
  } else if (e.type === 'lms') {
    const needMonths = v.lms_kind === 'next_month' || (v.lms_kind === 'new_path' && v.lms_scope === 'months');
    html = `${needMonths ? inp('gkeMonths', 'Month number(s)', (v.months || []).join(','), 40) : ''}
      <div class="form-group"><label for="gkeNote">Note</label><textarea id="gkeNote" rows="2" maxlength="1000" style="width:100%;max-width:560px;">${esc(v.note || '')}</textarea></div>`;
  } else {
    html = `<div class="form-group"><label for="gkeReason">Reason</label><select id="gkeReason">${['graduated', 'left', 'other'].map(x => `<option value="${x}"${v.reason === x ? ' selected' : ''}>${x}</option>`).join('')}</select></div>
      <div class="form-group"><label for="gkeNote">Note</label><textarea id="gkeNote" rows="2" maxlength="1000" style="width:100%;max-width:560px;">${esc(v.note || '')}</textarea></div>`;
  }
  modal.innerHTML = `<div class="modal-content" style="max-width:720px;">
    <button type="button" class="close-modal" id="gkeClose">&times;</button>
    <h2>Edit ${esc(d.request.request_no)}</h2>
    <p class="page-intro">The change is recorded in the request's history${d.request ? ' and the person who submitted it is told' : ''}.</p>
    ${html}
    <div class="form-group"><label for="gkeWhy">Why (optional)</label><input type="text" id="gkeWhy" maxlength="300" style="max-width:560px;width:100%;"></div>
    <div class="btn-group" style="display:flex;gap:8px;justify-content:flex-end;"><button type="button" class="secondary" id="gkeCancel">Cancel</button><button type="button" id="gkeSave">Save changes</button></div>
  </div>`;
  modal.classList.add('active');
  const close = () => modal.classList.remove('active');
  document.getElementById('gkeClose').onclick = close;
  document.getElementById('gkeCancel').onclick = close;

  // child rows (add_user only)
  let kidState = (v.child_paths || []).map(c => ({ path_ids: [...c.path_ids], scope: c.scope, months: c.months || [] }));
  const readKids = () => [...document.querySelectorAll('#gkeChildRows .gke-kid')].map((row, i) => ({
    path_ids: [...row.querySelectorAll('.gke-path:checked')].map(b => b.value),
    scope: row.querySelector(`input[name="gkeScope${i}"]:checked`)?.value === 'months' ? 'months' : 'entire',
    months: (row.querySelector('.gke-months')?.value || '').split(/[\s,]+/).filter(Boolean).map(Number),
  }));
  const renderKids = () => {
    const box = document.getElementById('gkeChildRows');
    if (!box) return;
    const parent = [...document.querySelectorAll('.gke-role:checked')].some(b => b.value === 'parent');
    document.getElementById('gkeKidsBox').style.display = parent ? '' : 'none';
    const n = Math.max(0, Math.min(20, parseInt(document.getElementById('gkeKids').value, 10) || 0));
    const prog = e.options.programs.find(p => p.id === document.getElementById('gkeProgram').value);
    if (!parent || !n || !prog) { box.innerHTML = ''; return; }
    box.innerHTML = Array.from({ length: n }, (_, i) => {
      const k = kidState[i] || { path_ids: [], scope: 'entire', months: [] };
      return `<div class="gke-kid" style="border:1px solid var(--border);border-radius:8px;padding:8px 12px;margin-bottom:8px;">
        <strong>Child ${i + 1}</strong>
        <div style="display:flex;flex-wrap:wrap;gap:4px 16px;margin:6px 0;">${prog.paths.map(p => `<label style="display:flex;gap:6px;align-items:center;font-weight:normal;"><input type="checkbox" class="gke-path" value="${p.id}"${k.path_ids.includes(p.id) ? ' checked' : ''}> ${esc(p.name)}</label>`).join('')}</div>
        <label style="font-weight:normal;"><input type="radio" name="gkeScope${i}" value="entire"${k.scope !== 'months' ? ' checked' : ''}> Entire path</label>
        <label style="font-weight:normal;margin-left:12px;"><input type="radio" name="gkeScope${i}" value="months"${k.scope === 'months' ? ' checked' : ''}> Only month(s):</label>
        <input type="text" class="gke-months" value="${esc((k.months || []).join(','))}" maxlength="40" style="max-width:120px;">
      </div>`;
    }).join('');
  };
  if (e.type === 'add_user') {
    renderKids();
    document.querySelectorAll('.gke-role').forEach(b => b.addEventListener('change', renderKids));
    document.getElementById('gkeKids').addEventListener('input', () => { kidState = readKids(); renderKids(); });
    document.getElementById('gkeProgram').addEventListener('change', () => { kidState = []; renderKids(); });
  }

  document.getElementById('gkeSave').onclick = async () => {
    let changes;
    if (e.type === 'add_user') {
      const roles = [...document.querySelectorAll('.gke-role:checked')].map(b => b.value);
      changes = { first_name: document.getElementById('gkeFirst').value, last_name: document.getElementById('gkeLast').value,
        email: document.getElementById('gkeEmail').value, gender: document.querySelector('input[name="gkeGender"]:checked')?.value,
        roles, team_id: document.getElementById('gkeTeam').value || null, approver_notes: document.getElementById('gkeNotes').value };
      if (roles.includes('parent')) {
        changes.num_children = Number(document.getElementById('gkeKids').value);
        changes.program_id = document.getElementById('gkeProgram').value;
        changes.child_paths = readKids();
        const missing = changes.child_paths.findIndex(k => !k.path_ids.length);
        if (changes.num_children > 0 && missing >= 0) { showToast(`Child ${missing + 1}: choose at least one Learning Path`, 'error'); return; }
      }
    } else if (e.type === 'add_guardian') {
      changes = { first_name: document.getElementById('gkeFirst').value, last_name: document.getElementById('gkeLast').value,
        email: document.getElementById('gkeEmail').value, relationship_label: document.getElementById('gkeRel').value };
    } else if (e.type === 'lms') {
      changes = { note: document.getElementById('gkeNote').value };
      const m = document.getElementById('gkeMonths');
      if (m) changes.months = m.value.split(/[\s,]+/).filter(Boolean).map(Number);
    } else {
      changes = { reason: document.getElementById('gkeReason').value, note: document.getElementById('gkeNote').value };
    }
    if (!(await showConfirm(`Save the changes to <strong>${esc(d.request.request_no)}</strong>?`))) return;
    try {
      await api('/api/gurukul/requests', { method: 'POST', body: JSON.stringify({ id: d.request.id, action: 'edit', changes, comment: document.getElementById('gkeWhy').value }) });
      close();
      showToast('Request updated', 'success');
      await openDetail(d.request.id);
      if (typeof loadList === 'function') loadList();
    } catch (err) { showToast(err.message, 'error'); }
  };
}

function openAction(action, request) {
  const a = ACTIONS[action];
  const modal = document.getElementById('gkrActionModal');
  if (modal.parentElement !== document.body) document.body.appendChild(modal);
  document.getElementById('gkrActionTitle').textContent = a.title;
  document.getElementById('gkrActionRef').textContent = `${request.request_no} — ${request.type_label}`;
  document.getElementById('gkrActionHint').textContent = a.hint;
  const text = document.getElementById('gkrActionText');
  text.value = '';
  const confirm = document.getElementById('gkrActionConfirm');
  confirm.textContent = a.confirm;
  confirm.className = a.cls;
  confirm.disabled = false;
  modal.classList.add('active');
  document.getElementById('gkrActionCancel').onclick = () => modal.classList.remove('active');
  confirm.onclick = async () => {
    const comment = text.value.trim();
    if (a.needText && !comment) { showToast('Please fill in the text', 'error'); return; }
    confirm.disabled = true;
    try {
      await api('/api/gurukul/requests', { method: 'POST', body: JSON.stringify({ id: request.id, action, comment }) });
      modal.classList.remove('active');
      showToast({ approve: 'Approved', reject: 'Rejected', clarify: 'Question sent', reply: 'Reply sent' }[action], 'success');
      await loadList();
      await openDetail(request.id);
    } catch (err) {
      showToast(err.message, 'error');
      confirm.disabled = false;
    }
  };
}

export async function initGurukulRequestsPage() {
  ['gkrStatus', 'gkrType', 'gkrTeam'].forEach(id => document.getElementById(id).addEventListener('change', loadList));
  document.getElementById('gkrSearchBtn').addEventListener('click', loadList);
  document.getElementById('gkrSearch').addEventListener('keydown', e => { if (e.key === 'Enter') loadList(); });
  document.getElementById('gkrDetailClose').addEventListener('click', closeDetail);
  document.getElementById('gkrNewLms').addEventListener('click', openNewLms);

  // Deep link from a notification: /gurukul/requests?open=<id> — show all so the request is found.
  const open = new URLSearchParams(window.location.search).get('open');
  if (open) document.getElementById('gkrStatus').value = 'all';
  await loadList();
  if (open) openDetail(open);
}
